import { COMPACT_MODAL_MEDIA } from './ResponsiveModalLayout'
import { lightTheme } from '@/customTheme'
import Icons from '@/scenes/Editor/components/Icons'
import { Modal, ROLE, SIZE } from 'baseui/modal'
import React from 'react'
import { useStyletron } from 'styletron-react'

interface Props {
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
  hasCloseButton?: boolean
  /** Allow Escape and backdrop dismissal without adding the Base Web close icon. */
  dismissOnEscapeOrBackdrop?: boolean
  /** Keep keyboard interaction inside the dialog while it owns a pending action. */
  focusLock?: boolean
  /** Dialog width. Defaults to the 920px two-column frame. */
  width?: string
  /** Dialog height. Pass `'auto'` for content-driven dialogs. */
  height?: string
  /** Dialog corner radius. */
  borderRadius?: string
}

function ModalWrapper({
  isOpen,
  onClose,
  children,
  hasCloseButton = true,
  dismissOnEscapeOrBackdrop = false,
  focusLock = false,
  width = '920px',
  height = '652px',
  borderRadius = '16px',
}: Props) {
  const [css] = useStyletron()

  return (
    <Modal
      onClose={onClose}
      isOpen={isOpen}
      animate
      closeable={dismissOnEscapeOrBackdrop}
      focusLock={focusLock}
      returnFocus={focusLock}
      size={SIZE.default}
      role={ROLE.dialog}
      overrides={{
        Close: { component: () => null },
        Backdrop: {
          style: () => ({ ...lightTheme.modal.BackdropStyle }),
        },
        Root: {
          style: () => ({ display: 'flex', flexDirection: 'column', zIndex: 10000 }),
        },
        Dialog: {
          style: () => ({
            width,
            height,
            borderRadius,
            overflow: 'hidden',
            padding: '0',
            margin: '0',
            position: 'relative',
            boxShadow: '0px 0px 6px 0px rgba(0,0,0,0.08)',
            [COMPACT_MODAL_MEDIA]: {
              maxWidth: 'calc(100vw - 24px)',
              maxHeight: 'calc(100dvh - 24px)',
              overflowY: 'auto',
              overscrollBehavior: 'contain',
            },
          }),
        },
      }}
    >
      {children}
      {hasCloseButton && (
        <div
          onClick={onClose}
          className={css({
            position: 'absolute',
            top: '16px',
            right: '16px',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: 'rgba(0,0,0,0.5)',
            opacity: 0.5,
            transition: 'opacity 0.15s ease, background 0.1s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 1,
            ':hover': {
              opacity: 1
            },
            ':active': {
              opacity: 1,
              background: 'rgba(0,0,0,0.7)'
            },
          })}
        >
          <Icons.Close size={24} fill={lightTheme.colors.white} />
        </div>
      )}
    </Modal>
  )
}

export default ModalWrapper
